/* eslint-disable */
// /src/pages/SalesMinePage.jsx
import React, { useMemo, useState, useEffect } from "react";
import styled, { createGlobalStyle } from "styled-components";
import BrandHeader from "../components/BrandHeader";
import PageTopBar from "../components/PageTopBar";
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
} from "recharts";
import { useMember } from "../context/MemberContext";
import bearImg from "../assets/images/character_bear.png";
import { fetchMonthlyFaresByFranchise } from "../services/settlementsService";
import { useDriverStatusContext } from "context/DriverStatusContext";

/* ========= Global: Barcode Font ========= */
const GlobalBarcodeFont = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Libre+Barcode+39&display=swap');
`;

/* ========= Layout ========= */
const Wrap = styled.div`
  min-height: 100vh;
  background: #eaf3ff;
  display: flex;
  flex-direction: column;
`;
const Sheet = styled.div`
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
`;
const Body = styled.div`
  flex: 1;
  display: grid;
  grid-template-rows: auto auto auto 1fr auto;
  row-gap: 14px;
  padding: 16px;
`;

/* ========= Year Control ========= */
const YearRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  padding: 0 4px;
`;
const YearControl = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
`;
const YearBtn = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 999px;
  border: 0;
  cursor: pointer;
  display: grid;
  place-items: center;
  background: #e5ebff;
  color: #1e293b;
  font-size: 16px;
  font-weight: 700;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.12);
  &:active {
    transform: translateY(1px);
  }
`;
const YearText = styled.span`
  min-width: 70px;
  text-align: center;
`;

/* ========= Chart Card ========= */
const ChartCard = styled.div`
  position: relative;
  border-radius: 20px;
  padding: 16px;
  height: 420px;
  overflow: hidden;
  background:
    radial-gradient(140% 90% at 50% -10%, rgba(255, 255, 255, 0.18) 0%, rgba(255, 255, 255, 0) 55%),
    radial-gradient(60% 40% at 20% 25%, rgba(110, 170, 255, 0.35) 0%, rgba(110, 170, 255, 0) 60%),
    radial-gradient(45% 35% at 80% 30%, rgba(140, 190, 255, 0.28) 0%, rgba(140, 190, 255, 0) 60%),
    linear-gradient(180deg, #1846cc 0%, #1b4be0 40%, #0f2f9a 100%);
  box-shadow:
    inset 0 0 120px rgba(0, 0, 0, 0.25),
    inset 0 0 40px rgba(0, 0, 0, 0.18);
  &::after {
    content: "";
    position: absolute;
    inset: -30%;
    background: repeating-linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.06) 0 8px,
      rgba(255, 255, 255, 0) 8px 22px
    );
    opacity: 0.2;
    pointer-events: none;
    transform: rotate(2deg);
  }
`;

/* ========= Monthly Table ========= */
const TableCard = styled.section`
  margin-top: 12px;
  background: #ffffff;
  padding: 10px 10px 8px;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.07);
  font-family: "Pretendard", var(--font-round);
`;

const TableTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 4px 4px 8px;
`;

const TableTitle = styled.div`
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
`;

const TableCaption = styled.div`
  font-size: 11px;
  color: #6b7280;
`;

const TableHeaderRow = styled.div`
  display: grid;
  grid-template-columns: 70px 1.3fr 0.9fr;
  align-items: center;
  padding: 8px 10px;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  border-radius: 14px;
  background: #eef2ff;
`;

const TableBodyWrap = styled.div`
  margin-top: 4px;
  border-radius: 16px;
  background: #f8fafc;
  overflow: hidden;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 70px 1.3fr 0.9fr;
  align-items: center;
  padding: 9px 10px;
  font-size: 13px;
  line-height: 1.35;
  color: #0f172a;
  background: ${(p) => (p.$odd ? "rgba(255, 255, 255, 0.95)" : "rgba(248, 250, 252, 0.98)")};
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
`;

const CellMonth = styled.div`
  font-weight: 800;
  color: #0f172a;
`;

const CellSales = styled.div`
  text-align: right;
  font-weight: 800;
  .main {
    font-size: 14px;
  }
`;

const CallFeeText = styled.div`
  margin-top: 2px;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
`;

// 🔹 전월 대비 텍스트 — 칩 대신 작은 텍스트만
const DiffText = styled.div`
  text-align: right;
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
  color: ${(p) =>
        p.$type === "up"
            ? "#15803d"
            : p.$type === "down"
                ? "#b91c1c"
                : "#9ca3af"};

  .arrow {
    font-size: 11px;
    margin-right: 2px;
  }
`;

/* ========= Summary ========= */
const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;
const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 1px 10px rgba(0, 0, 0, 0.06);
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const Label = styled.div`
  font-size: 12px;
  color: #5b6b8b;
`;
const Value = styled.div`
  font-size: 18px;
  color: #0f172a;
  font-family: "Pretendard", var(--font-round);
`;

const BottomPad = styled.div`
  height: max(24px, var(--sat, env(safe-area-inset-bottom)));
`;

/* ========= Owned Membership Card ========= */
const OwnedCard = styled.div`
  margin-top: 8px;
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  background: ${(p) => p.$bg || "#2cb3f0"};
  box-shadow:
    0 6px 18px rgba(0, 0, 0, 0.1),
    inset 0 0 0 2px rgba(255, 255, 255, 0.12);
  padding: 16px 16px 20px;
  min-height: 220px;
  align-items: center;
  display: flex;
  flex-direction: column;
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      80% 60% at 20% 10%,
      rgba(255, 255, 255, 0.25) 0%,
      rgba(255, 255, 255, 0) 70%
    );
    pointer-events: none;
  }
`;
const OwnedTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 10px;
`;
const OwnedTitle = styled.div`
  color: #111;
  font-size: 14px;
`;
const OwnedName = styled.div`
  color: #111;
  font-size: 18px;
`;
const OwnedIdPill = styled.div`
  font-family: "Pretendard", var(--font-round);
  color: #111;
  letter-spacing: 0.04em;
  font-size: 16px;
  background: rgba(255, 255, 255, 0.95);
  padding: 6px 12px;
  border-radius: 10px;
`;
const OwnedFigure = styled.div`
  width: 100%;
  display: grid;
  place-items: center;
  img {
    height: 140px;
    width: auto;
    object-fit: contain;
    filter: drop-shadow(0 6px 10px rgba(0, 0, 0, 0.12));
  }
`;

/* ========= Barcode (카드 안) ========= */
const BarcodeTapArea = styled.button`
  margin-top: 6px;
  border: 0;
  background: transparent;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
`;

const BarcodeBox = styled.div`
  display: inline-block;
  border-radius: 10px;
  padding: 6px 10px;
  transform: scaleX(0.7);
  background: rgba(255, 255, 255, 0.9);
`;

const Barcode = styled.div`
  font-family: "Libre Barcode 39", monospace;
  font-style: normal;
  font-weight: 400;
  font-size: 42px;
  line-height: 1;
  letter-spacing: 0;
  color: #000;
  white-space: nowrap;
`;

const BarcodeHint = styled.div`
  margin-top: 6px;
  font-size: 11px;
  color: #111827;
  background: rgba(255, 255, 255, 0.9);
  padding: 4px 8px;
  border-radius: 999px;
`;

/* ========= 전체 화면 바코드 모달 (가로 카드 레이아웃) ========= */
const BarcodeOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(15, 23, 42, 0.82);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const BarcodeModalInner = styled.div`
  width: 100%;
  max-width: 520px;
  padding: 24px 20px 32px;
  box-sizing: border-box;
`;

const BarcodeModalCard = styled.div`
  position: relative;
  background: ${(p) => p.$bg || "#f9fafb"};
  border-radius: 28px;
  padding: 20px 18px 24px;
  box-shadow: 0 24px 50px rgba(0, 0, 0, 0.45);
  overflow: hidden;
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      110% 90% at 10% 0%,
      rgba(255, 255, 255, 0.45) 0%,
      rgba(255, 255, 255, 0) 55%
    );
    pointer-events: none;
  }
`;

const BarcodeModalHeader = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  z-index: 1;
`;

const BarcodeModalTitle = styled.div`
  font-size: 16px;
  font-weight: 800;
  color: #0f172a;
`;

const CloseBtn = styled.button`
  border: 0;
  background: transparent;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  color: #111827;
  &:active {
    transform: translateY(1px);
  }
`;

/* 가로 카드 본문 */
const EnlargedCardBody = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const EnlargedTopRow = styled(OwnedTopRow)`
  margin-top: 0;
`;

const EnlargedMainRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  @media (max-width: 420px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const BigBarcodeBox = styled.div`
  flex: 1;
  border-radius: 20px;
  background: #ffffff;
  padding: 16px 14px 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden; /* 🔹 바코드가 박스 밖으로 안 튀게 */
`;

const BigBarcode = styled.div`
  font-family: "Libre Barcode 39", monospace;
  font-style: normal;
  font-weight: 400;
  font-size: 56px;          /* 🔹 조금 줄임 */
  line-height: 1;
  color: #000;
  transform: scaleX(0.7);   /* 🔹 가로 압축해서 카드 안에 들어오게 */
  white-space: nowrap;
`;

const BigBarcodeNumber = styled.div`
  margin-top: 12px;
  font-size: 16px;
  font-weight: 800;
  letter-spacing: 0.12em;
  color: #111827;
`;

const EnlargedMascotWrap = styled.div`
  width: 120px;
  min-width: 100px;
  display: grid;
  place-items: center;
  img {
    width: 100%;
    height: auto;
    object-fit: contain;
    filter: drop-shadow(0 10px 16px rgba(15, 23, 42, 0.25));
  }
`;

const BarcodeModalHint = styled.div`
  margin-top: 14px;
  font-size: 12px;
  color: #111827;
  text-align: center;
  line-height: 1.5;
`;

/* ========= Helpers ========= */
const toKRW = (n) => Number(n || 0).toLocaleString();

function makeDisplayId(phoneE164 = "") {
    const digits = (phoneE164.match(/\d/g) || []).join("").slice(-12).padStart(12, "0");
    const prefix = "91";
    const checksum = digits.split("").reduce((a, b) => a + Number(b), 0) % 97;
    const checksumStr = checksum.toString().padStart(2, "0");
    return (prefix + digits + checksumStr).replace(/(\d{4})(?=\d)/g, "$1-");
}

// 🔹 증감 계산: 현재 달 매출이 0이면 아예 표시 안 함 (– 처리)
function formatDiff(prev, curr) {
    if (curr == null || curr === 0) {
        return { text: "", type: "flat" };
    }
    if (prev == null || prev === 0) {
        return { text: "–", type: "flat" };
    }
    const diff = curr - prev;
    if (diff === 0) {
        return { text: "0.0%", type: "flat" };
    }
    const pct = (diff / prev) * 100;
    const sign = diff > 0 ? "+" : "-";
    const text = `${sign}${Math.abs(pct).toFixed(1)}%`;
    return {
        text,
        type: diff > 0 ? "up" : "down",
    };
}

// grade → 색상/라벨
const COLORS = {
    guest: "#EDEDED",
    orange: "#FF5A00",
    purple: "#A64AC9",
    emerald: "#2CB3F0",
};
const mapGradeToTier = (grade) => {
    switch (grade) {
        case "orange":
            return "오렌지";
        case "purple":
            return "퍼플";
        case "emerald":
            return "에메랄드";
        case "guest":
        default:
            return "게스트";
    }
};

// 🔹 settlementMonthKey 파싱: "2025-10" / "202510" → { year, month, label }
function parseSettlementMonthKey(key) {
    if (!key) return null;
    const raw = String(key).trim();
    let year;
    let month;

    if (/^\d{4}-\d{2}$/.test(raw)) {
        const parts = raw.split("-");
        year = Number(parts[0]);
        month = Number(parts[1]);
    } else if (/^\d{6}$/.test(raw)) {
        year = Number(raw.slice(0, 4));
        month = Number(raw.slice(4));
    } else {
        return null;
    }

    if (!year || !month || month < 1 || month > 12) return null;
    return {
        year,
        month,
        label: `${month}월`,
    };
}

export default function SalesMinePage() {
    const [year, setYear] = useState(new Date().getFullYear());

    const {
        franchisename,
        grade,
        membershipBarcode,
        name: dsName,
        phoneE164: dsPhoneE164,
    } = useDriverStatusContext();

    const mc = (() => {
        try {
            return useMember?.();
        } catch {
            return null;
        }
    })();
    const ctxName = mc?.member?.name;
    const ctxPhone = mc?.member?.phoneE164;

    const name = dsName || ctxName || "홍길동";
    const phoneE164 = dsPhoneE164 || ctxPhone || "+821012341234";

    const tierLabel = mapGradeToTier(grade);
    const membershipBg = COLORS[grade] || COLORS.guest;
    const memberId = useMemo(() => {
        const raw = membershipBarcode || "";
        if (raw && /^\d{12}$/.test(raw)) {
            return raw.replace(/(\d{4})(?=\d)/g, "$1-");
        }
        return makeDisplayId(phoneE164);
    }, [membershipBarcode, phoneE164]);

    const barcodeValue = useMemo(
        () => memberId.replace(/-/g, ""),
        [memberId]
    );
    const [barcodeOpen, setBarcodeOpen] = useState(false);

    const [monthly, setMonthly] = useState([]);
    const [loadingMonthly, setLoadingMonthly] = useState(true);
    const [monthlyErr, setMonthlyErr] = useState("");

    const [latestSettleInfo, setLatestSettleInfo] = useState(null);

    useEffect(() => {
        let alive = true;

        (async () => {
            if (!franchisename) {
                setMonthly([]);
                setLatestSettleInfo(null);
                setLoadingMonthly(false);
                setMonthlyErr("정식 가맹 정보가 없어 매출을 집계할 수 없습니다.");
                return;
            }
            try {
                setLoadingMonthly(true);
                setMonthlyErr("");
                setLatestSettleInfo(null);

                const rows = await fetchMonthlyFaresByFranchise(franchisename, year);
                if (!alive) return;

                const pureRows = Array.isArray(rows) ? rows : [];
                setMonthly(pureRows);

                const latestKey = rows && rows.latestMonthKey;
                if (latestKey) {
                    const parsed = parseSettlementMonthKey(latestKey);
                    if (parsed) {
                        const targetRow =
                            pureRows.find((r) => r.month === parsed.month) || null;
                        const sales = targetRow?.sales || 0;
                        setLatestSettleInfo({
                            label: parsed.label,
                            sales,
                        });
                    } else {
                        setLatestSettleInfo(null);
                    }
                } else {
                    setLatestSettleInfo(null);
                }

                const hasAny = (pureRows || []).some((r) => (r.sales || 0) > 0);
                if (!hasAny) {
                    setMonthlyErr(`${year}년 매출 데이터가 없습니다.`);
                }
            } catch (e) {
                console.error(e);
                if (!alive) return;
                setMonthly([]);
                setLatestSettleInfo(null);
                setMonthlyErr("월별 매출 데이터를 불러오는 중 오류가 발생했습니다.");
            } finally {
                if (alive) setLoadingMonthly(false);
            }
        })();

        return () => {
            alive = false;
        };
    }, [franchisename, year]);

    const chartData = monthly;
    const monthlyRows = useMemo(
        () =>
            monthly.map((d, idx) => {
                const prev = idx > 0 ? monthly[idx - 1].sales : null;
                const diff = formatDiff(prev, d.sales);
                return { ...d, diff };
            }),
        [monthly]
    );

    const total = useMemo(
        () => monthly.reduce((a, b) => a + (b.sales || 0), 0),
        [monthly]
    );

    const latestSettledRow = useMemo(() => {
        if (!monthly || !monthly.length) return null;
        const nonZero = monthly.filter((m) => (m.sales || 0) > 0);
        if (!nonZero.length) return null;
        return nonZero[nonZero.length - 1];
    }, [monthly]);

    const settledMonthCount = useMemo(() => {
        if (latestSettledRow && typeof latestSettledRow.month === "number") {
            return latestSettledRow.month;
        }
        return monthly.length;
    }, [latestSettledRow, monthly.length]);

    const avg = useMemo(() => {
        if (!settledMonthCount) return 0;
        return Math.round(total / settledMonthCount);
    }, [total, settledMonthCount]);

    const hasChartData =
        chartData && chartData.length && chartData.some((d) => d.sales > 0);

    const tooltipStyle = {
        background: "rgba(10,20,60,.92)",
        border: "none",
        borderRadius: 10,
        color: "#fff",
    };
    const CustomTooltip = ({ active, payload, label }) =>
        active && payload?.length ? (
            <div style={tooltipStyle}>
                <div style={{ padding: "10px 12px" }}>
                    <div style={{ color: "#9db4ff", marginBottom: 4 }}>{label}</div>
                    <div>월 매출: {payload[0].value.toLocaleString()}원</div>
                </div>
            </div>
        ) : null;

    return (
        <Wrap>
            <GlobalBarcodeFont />
            <Sheet>
                <PageTopBar variant="back" label="뒤로" borderColor="#dde7fb" />

                <Body>
                    <BrandHeader subtitle="내 월별 매출" />
                    <YearRow>
                        <YearControl>
                            <YearBtn
                                onClick={() => setYear((y) => y - 1)}
                                aria-label="이전 연도"
                            >
                                ‹
                            </YearBtn>
                            <YearText>{year}년</YearText>
                            <YearBtn
                                onClick={() => setYear((y) => y + 1)}
                                aria-label="다음 연도"
                            >
                                ›
                            </YearBtn>
                        </YearControl>
                    </YearRow>

                    <ChartCard>
                        {loadingMonthly ? (
                            <div
                                style={{
                                    color: "#e5edff",
                                    fontSize: 14,
                                    padding: 16,
                                    position: "relative",
                                    zIndex: 1,
                                }}
                            >
                                월별 매출 데이터를 불러오는 중…
                            </div>
                        ) : !hasChartData ? (
                            <div
                                style={{
                                    color: "#e5edff",
                                    fontSize: 14,
                                    padding: 16,
                                    position: "relative",
                                    zIndex: 1,
                                }}
                            >
                                {monthlyErr || `${year}년 매출 데이터가 없습니다.`}
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart
                                    data={chartData}
                                    margin={{ top: 18, right: 18, left: 8, bottom: 10 }}
                                >
                                    <defs>
                                        <linearGradient id="gradFill" x1="0" y1="0" x2="0" y2="1">
                                            <stop
                                                offset="0%"
                                                stopColor="#FFFFFF"
                                                stopOpacity={0.95}
                                            />
                                            <stop
                                                offset="100%"
                                                stopColor="#FFFFFF"
                                                stopOpacity={0.1}
                                            />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid
                                        stroke="rgba(255,255,255,.18)"
                                        strokeDasharray="4 6"
                                        vertical={false}
                                    />
                                    <XAxis
                                        dataKey="label"
                                        tick={{ fill: "#fff", fontSize: 13 }}
                                        tickMargin={8}
                                    />
                                    <YAxis hide />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Area
                                        type="monotone"
                                        dataKey="sales"
                                        stroke="#FFFFFF"
                                        strokeWidth={3}
                                        fill="url(#gradFill)"
                                        activeDot={{
                                            r: 5,
                                            fill: "#fff",
                                            stroke: "#0f2f9a",
                                            strokeWidth: 2,
                                        }}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </ChartCard>

                    {/* 월별 테이블 */}
                    <TableCard>
                        <TableTitleRow>
                            <TableTitle>{year}년 월별 매출</TableTitle>
                            <TableCaption>월 매출 · 호출료 · 전월 대비 증감</TableCaption>
                        </TableTitleRow>

                        <TableHeaderRow>
                            <div>월</div>
                            <div style={{ textAlign: "right" }}>월 매출 / 호출료</div>
                            <div style={{ textAlign: "right" }}>전월 대비</div>
                        </TableHeaderRow>

                        <TableBodyWrap>
                            {loadingMonthly ? (
                                <div
                                    style={{
                                        padding: "10px 10px",
                                        fontSize: 13,
                                        color: "#64748b",
                                    }}
                                >
                                    월별 매출 데이터를 불러오는 중…
                                </div>
                            ) : monthlyRows.length === 0 ||
                                !monthlyRows.some((r) => (r.sales || 0) > 0) ? (
                                <div
                                    style={{
                                        padding: "10px 10px",
                                        fontSize: 13,
                                        color: "#64748b",
                                    }}
                                >
                                    {monthlyErr || `${year}년 매출 데이터가 없습니다.`}
                                </div>
                            ) : (
                                monthlyRows.map((row, idx) => {
                                    const arrow =
                                        row.diff.type === "up"
                                            ? "▲"
                                            : row.diff.type === "down"
                                                ? "▼"
                                                : "";
                                    const showDiff = row.diff.text && row.diff.text !== "–";
                                    return (
                                        <TableRow key={row.month} $odd={idx % 2 === 0}>
                                            <CellMonth>{row.label}</CellMonth>
                                            <CellSales>
                                                <div className="main">{toKRW(row.sales)}원</div>
                                                <CallFeeText>
                                                    호출료 {toKRW(row.call)}원
                                                </CallFeeText>
                                            </CellSales>
                                            <DiffText $type={row.diff.type}>
                                                {showDiff ? (
                                                    <>
                                                        {arrow && (
                                                            <span className="arrow">{arrow}</span>
                                                        )}
                                                        <span>{row.diff.text}</span>
                                                    </>
                                                ) : (
                                                    <span>–</span>
                                                )}
                                            </DiffText>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBodyWrap>
                    </TableCard>

                    {/* 요약 */}
                    <div style={{ height: 30 }} />
                    <BrandHeader subtitle="요약" />
                    <SummaryGrid>
                        <Card>
                            <Label>{year}년 총 매출</Label>
                            <Value>{toKRW(total)}원</Value>
                        </Card>
                        <Card>
                            <Label>월 평균 매출</Label>
                            <Value>{toKRW(avg)}원</Value>
                        </Card>
                        <Card>
                            <Label>
                                최근 정산달{latestSettledRow?.label || ""} 매출
                            </Label>
                            <Value>
                                {latestSettledRow ? (
                                    <>{toKRW(latestSettledRow.sales)}원</>
                                ) : (
                                    "-"
                                )}
                            </Value>
                        </Card>
                        <Card>
                            <Label>집계 개월 수</Label>
                            <Value>{monthly.length}개월</Value>
                        </Card>
                    </SummaryGrid>

                    <div style={{ height: 16 }} />

                    {/* 보유 멤버십 */}
                    <BrandHeader subtitle="보유 멤버십" />
                    <OwnedCard $bg={membershipBg}>
                        <OwnedTopRow>
                            <OwnedTitle>Membership Card • {tierLabel}</OwnedTitle>
                        </OwnedTopRow>
                        <OwnedTopRow>
                            <OwnedName>{name}</OwnedName>
                            <OwnedIdPill>{memberId}</OwnedIdPill>
                        </OwnedTopRow>

                        <BarcodeTapArea onClick={() => setBarcodeOpen(true)}>
                            <BarcodeBox>
                                <Barcode aria-label={`barcode-${memberId}`}>
                                    {`*${barcodeValue}*`}
                                </Barcode>
                            </BarcodeBox>
                            <BarcodeHint>바코드를 터치하면 크게 볼 수 있어요</BarcodeHint>
                        </BarcodeTapArea>

                        <OwnedFigure>
                            <img src={bearImg} alt="membership mascot" />
                        </OwnedFigure>
                    </OwnedCard>

                    <BottomPad />
                </Body>
            </Sheet>

            {/* 전체 화면 바코드 모달 */}
            {barcodeOpen && (
                <BarcodeOverlay>
                    <BarcodeModalInner>
                        <BarcodeModalCard $bg={membershipBg}>
                            <BarcodeModalHeader>
                                <BarcodeModalTitle>멤버십 바코드</BarcodeModalTitle>
                                <CloseBtn
                                    type="button"
                                    onClick={() => setBarcodeOpen(false)}
                                    aria-label="닫기"
                                >
                                    ✕
                                </CloseBtn>
                            </BarcodeModalHeader>

                            <EnlargedCardBody>
                                <EnlargedTopRow>
                                    <OwnedTitle>Membership Card • {tierLabel}</OwnedTitle>
                                </EnlargedTopRow>
                                <EnlargedTopRow>
                                    <OwnedName>{name}</OwnedName>
                                    <OwnedIdPill>{memberId}</OwnedIdPill>
                                </EnlargedTopRow>

                                <EnlargedMainRow>
                                    <BigBarcodeBox>
                                        <BigBarcode>{`*${barcodeValue}*`}</BigBarcode>
                                        <BigBarcodeNumber>{memberId}</BigBarcodeNumber>
                                    </BigBarcodeBox>

                                    <EnlargedMascotWrap>
                                        <img src={bearImg} alt="membership mascot large" />
                                    </EnlargedMascotWrap>
                                </EnlargedMainRow>

                                <BarcodeModalHint>
                                    화면 밝기를 최대로 올리고,
                                    <br />
                                    스캐너에 가로 방향으로 바코드를 보여 주세요.
                                </BarcodeModalHint>
                            </EnlargedCardBody>
                        </BarcodeModalCard>
                    </BarcodeModalInner>
                </BarcodeOverlay>
            )}
        </Wrap>
    );
}
